import _ from 'underscore';
import Promise from 'bluebird';
import { http } from '@bingads-webui/http-util';
import { getSmartPageUrlSuffix } from '@bingads-webui-campaign/smart-page-url-suffix-provider';
import {
  modelToSiteConfig,
  facebookPageToSiteConfig,
} from '@bingads-webui-campaign-react/smart-component-util';

import { getCommonOdataOptions } from './odata-options';
import { wrappedOdata as odata } from './odata-wrapper';
import { SmartPageAction, SmartPageStatus, siteConfigToModel } from './service-utils';

export const SmartPageService = ({
  serverContext,
}) => {
  const service = {};
  const {
    cid,
    aid,
    host,
    path,
    token,
    SignOutUrl,
  } = getCommonOdataOptions({ serverContext });

  const initOption = {
    cid,
    aid,
    host,
    path,
    token,
    SignOutUrl,
  };
  if (SmartPageService.service && _.isEqual(SmartPageService.initOption, initOption)) {
    return SmartPageService.service;
  }
  SmartPageService.initOption = initOption;

  const baseUrl = `Customers(${cid})/Accounts(${aid})/SmartPages`;
  const defaultQuery = {
    $top: 20,
    $skip: 0,
    $count: true,
  };
  const pollRetryTimespan = 3000;
  const maxRetry = 20;

  const oDataOption = {
    contentType: 'application/json',
    host,
    path,
    oDataToken: token,
    SignOutUrl,
  };

  const getUrlWithId = id => `${baseUrl}('${id}')`;

  const pollCompletion = (id, retry, resolve, reject) => {
    if (retry <= 0) {
      reject('Publish pollCompletion timeout');
      return;
    }

    service.get(id)
      .then((data) => {
        if (data.status === 'Published') {
          // Upon successful publish, insert a call to ui server to force update cached
          // page version on subdomain, otherwise user would still see smart page content
          // before publishing. Best effort, do not check return on updateversion
          // do not interrupt the publish flow on updateversion failure
          // No response body in this request
          http.get(`https://${data.subDomain}${getSmartPageUrlSuffix(document.domain)}/updateversion`, { dataType: 'text' });

          resolve(data);
        } else {
          setTimeout(() => pollCompletion(id, retry - 1, resolve, reject), pollRetryTimespan);
        }
      }).catch(e => reject(e));
  };

  service.getAll = (customQuery) => {
    const query = _.extend({}, defaultQuery, customQuery);
    const options = _.extend(
      {},
      oDataOption,
      {
        url: baseUrl,
        data: query,
      }
    );

    return odata.get(options)
      .then(res => _.map(res.value, item => modelToSiteConfig(item, serverContext)));
  };

  service.get = (id, customQuery) => {
    const query = _.extend({}, customQuery);
    const options = _.extend(
      {},
      oDataOption,
      {
        url: getUrlWithId(id),
        data: query,
      }
    );

    return odata.get(options)
      .then(res => modelToSiteConfig(res, serverContext));
  };

  service.create = (siteConfig) => {
    const options = _.extend(
      {},
      oDataOption,
      {
        url: baseUrl,
        data: siteConfigToModel(siteConfig, SmartPageAction.None, serverContext),
      }
    );

    return odata.post(options)
      .then(res => ({ Id: res.value }));
  };

  service.update = (id, siteConfig, action = SmartPageAction.None) => {
    const options = _.extend(
      {},
      oDataOption,
      {
        url: getUrlWithId(id),
        data: siteConfigToModel(siteConfig, action, serverContext),
      }
    );

    return odata.patch(options)
      .then(res => modelToSiteConfig(res, serverContext));
  };

  service.delete = (id) => {
    const options = _.extend(
      {},
      oDataOption,
      {
        url: getUrlWithId(id),
      }
    );

    return odata.delete(options);
  };

  service.publish = (id, action = SmartPageAction.None) => {
    const options = _.extend(
      {},
      oDataOption,
      {
        url: getUrlWithId(id),
        data: {
          '@odata.type': '#Model.SmartPage',
          Status: SmartPageStatus.Published,
          Action: action,
        },
      }
    );

    return odata.patch(options)
      .then(() => ({ Id: id }));
  };

  service.pollStatus = id => new Promise((resolve, reject) => {
    pollCompletion(id, maxRetry, resolve, reject);
  });

  service.getFacebookPage = (fbPageId, fbAuthInfo, dataFilter, scenario) => {
    const url = `${baseUrl}/Default.GetFbPage`;
    const options = _.extend(
      {},
      oDataOption,
      {
        url,
        data: {
          fbPageAuthorizationData: {
            PageId: fbPageId,
            UserId: fbAuthInfo.fbUserId,
            Scenario: scenario,
            Content: dataFilter || [],
          },
        },
      }
    );

    return odata.post(options)
      .then(res => facebookPageToSiteConfig(res));
  };


  service.getFacebookPageList = (fbAuthInfo, dataFilter, scenario) => {
    const url = `${baseUrl}/Default.GetFbUserPages`;
    const options = _.extend(
      {},
      oDataOption,
      {
        url,
        data: {
          fbPageAuthorizationData: {
            UserToken: fbAuthInfo.fbToken,
            UserId: fbAuthInfo.fbUserId,
            Scenario: scenario,
            Content: dataFilter || [],
          },
        },
      }
    );

    return odata.post(options)
      .then(res => _.chain(res.value)
        .sortBy(pageInfo => pageInfo.Name)
        .map(pageInfo => facebookPageToSiteConfig(pageInfo))
        .value());
  };

  service.isSubdomainAvailable = (subdomain) => {
    const url = `${baseUrl}/Default.IsSubdomainAvailable(subdomain='${subdomain}')`;
    const options = _.extend(
      {},
      oDataOption,
      {
        url,
      }
    );

    return odata.get(options).then(res => res.value);
  };

  service.associateEntities = (id, entityIds, associatedEntityStatus) => {
    const url = `${getUrlWithId(id)}/Associate`;
    const options = _.extend(
      {},
      oDataOption,
      {
        url,
        data: {
          EntityIds: entityIds,
          AssociationType: 'DMCCampaign',
          ...(
            !_.isEmpty(associatedEntityStatus)
              ? { AssociatedEntityStatus: associatedEntityStatus }
              : {}
          ),
        },
      }
    );

    return odata.post(options).then(res => res.value);
  };

  service.disassociateEntities = (id, entityIds) => {
    const url = `${getUrlWithId(id)}/Disassociate`;
    const options = _.extend(
      {},
      oDataOption,
      {
        url,
        data: {
          EntityIds: entityIds,
          AssociationType: 'DMCCampaign',
        },
      }
    );

    return odata.post(options).then(res => res.value);
  };

  service.getAssociationsByEntityIds = (entityIds) => {
    const url = `${baseUrl}/GetAssociations`;
    const options = _.extend(
      {},
      oDataOption,
      {
        url,
        data: {
          EntityIds: entityIds,
          AssociationType: 'DMCCampaign',
        },
      }
    );

    return odata.post(options)
      .then(res => res.value);
  };

  service.getSmartPageMetrics = (id, startDate, endDate) => {
    const options = _.extend(
      {},
      oDataOption,
      {
        url: `${getUrlWithId(id)}/SmartPageMetrics`,
        data: {
          startDate,
          endDate,
        },
      }
    );

    return odata.post(options).then(res => _.first(res.value) || {});
  };

  const campaignOption = {
    ...oDataOption,
    path: '/Mca/V1',
  };

  const accountOption = {
    ...campaignOption,
  };

  const campaignBaseUrl = `Customers(${cid})/Accounts(${aid})/Campaigns`;
  const accountBaseUrl = `Customers(${cid})/Accounts(${aid})`;

  const campaignBaseUrlWithId = id => `${campaignBaseUrl}('${id}')`;

  const attachCampaignParams = (url, params) => {
    const paramStr = Object.entries(params).map(([key, value]) => [`$${key}=${Array.isArray(value) ? value.join(',') : value}`]).join('&');
    if (url.includes('?')) {
      return `${url}&${paramStr}`;
    }
    return `${url}?${paramStr}`;
  };

  service.getCampaign = id =>
    odata.get({
      ...campaignOption,
      url: attachCampaignParams(campaignBaseUrlWithId(id), {
        expand: [
          'Ads',
          'Targets',
          'Keywords',
          'SocialProfiles',
        ],
      }),
    });

  service.patchCampaign = (id, data) =>
    odata.patch({
      ...campaignOption,
      url: campaignBaseUrlWithId(id),
      data,
    });

  service.getCampaigns = () =>
    odata.get({
      ...campaignOption,
      url: campaignBaseUrl,
    }).then(value => (Array.isArray(value && value.value) ? value.value : []));

  service.getBudgetSuggestion = (id, data) =>
    odata.post({
      ...campaignOption,
      url: `${campaignBaseUrlWithId(id)}/Default.GetUPBudgetSuggestions`,
      data,
    });

  service.getSocialProfileIds = () =>
    odata.get({
      ...campaignOption,
      url: attachCampaignParams(`${accountBaseUrl}/SocialProfiles?useCache=true`, {
        filter: ['Facebook', 'Instagram', 'LinkedIn', 'Twitter'].map(type => `ChannelType eq Enum.ChannelType'${type}'`).join(' or '),
      }),
    }).then(res => _.map(res.value, profile => profile.Id));

  service.getActivitesCount = ({
    activityTypes,
    endDate,
    lastCheckPoint,
    profileIds,
    startDate,
  }) =>
    odata.post({
      ...campaignOption,
      url: `${accountBaseUrl}/Default.GetActivitiesCount?$count=true`,
      data: {
        activityTypes,
        endDate,
        lastCheckPoint,
        profileIds,
        startDate,
      },
    }).then(res => _.first(res.value) || 0);

  service.getAdsPerformanceMetrics = data =>
    odata.post({
      ...accountOption,
      url: `${accountBaseUrl}/Default.McaAdsPerformanceMetrics`,
      data,
    });

  service.getCategorySuggestions = data =>
    odata.post({
      ...accountOption,
      url: `${accountBaseUrl}/Default.GetCategorySuggestions`,
      data,
    }).then(res => res.value);

  service.getContentSuggestions = (businessName, keywords, resultOption, imageSource) => {
    const url = `${baseUrl}/Default.ContentSuggestions`;
    const options = _.extend(
      {},
      oDataOption,
      {
        url,
        data: {
          businessName,
          keywords,
          entity: resultOption,
          imageSource,
        },
      }
    );

    return odata.post(options)
      .then(res => _.first(res.value) || {});
  };

  service.getTemplateSuggestions = (categories) => {
    const url = `${baseUrl}/Default.TemplateSuggestions`;
    const options = _.extend(
      {},
      oDataOption,
      {
        url,
        data: { categories },
      }
    );

    return odata.post(options).then(res => res.value);
  };

  SmartPageService.service = service;

  return service;
};
